// Pure helpers for the Country Adventure modal (media URLs, theming,
// flag rendering, and GeoJSON -> SVG country-shape projection).
import { API } from '../../config';
import { COUNTRY_THEMES } from './constants';

let countryGeoPromise;

export function resolveMediaUrl(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${API}${url}`;
}

export function countryTheme(name = '') {
  const total = Array.from(name).reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  return COUNTRY_THEMES[total % COUNTRY_THEMES.length];
}

export function initialDiscoveryMedia(info) {
  const discoveries = info?.discoveries || {};
  return Object.fromEntries(
    Object.entries(discoveries)
      .filter(([, value]) => value?.imageUrl && value?.audioUrl)
      .map(([type, value]) => [type, value])
  );
}

export function countryFlagEmoji(code = '') {
  if (!/^[A-Z]{2}$/i.test(code)) return '\uD83C\uDF0D';
  return code
    .toUpperCase()
    .replace(/./g, char => String.fromCodePoint(127397 + char.charCodeAt(0)));
}

export function countryFlagUrl(info) {
  const code = info?.countryCode || info?.code;
  if (!/^[A-Z]{2}$/i.test(code || '')) return null;
  return info?.flagUrl || `/flags/4x3/${code.toLowerCase()}.svg`;
}

export function loadCountryGeojson() {
  if (!countryGeoPromise) {
    countryGeoPromise = fetch(`${API}/geo/countries.geojson`)
      .then((res) => {
        if (!res.ok) throw new Error(`Country map unavailable (${res.status})`);
        return res.json();
      });
  }
  return countryGeoPromise;
}

export function normalizeCountryName(name = '') {
  return String(name).toLowerCase().replace(/^the\s+/, '').replace(/[^a-z0-9]+/g, '');
}

export function featureMatchesCountry(feature, info) {
  const props = feature?.properties || {};
  const code = (info?.countryCode || info?.code || '').toUpperCase();
  const names = [
    props.NAME,
    props.NAME_LONG,
    props.NAME_EN,
    props.ADMIN,
    props.SOVEREIGNT,
    props.GEOUNIT,
  ].map(normalizeCountryName);

  return [
    props.ISO_A2,
    props.ISO_A2_EH,
    props.WB_A2,
    props.POSTAL,
  ].some((value) => String(value || '').toUpperCase() === code)
    || names.includes(normalizeCountryName(info?.name));
}

function adjustedLon(lon, crossesDateline) {
  return crossesDateline && lon < 0 ? lon + 360 : lon;
}

function getGeometryPoints(geometry) {
  const polygons = geometry?.type === 'Polygon'
    ? [geometry.coordinates]
    : geometry?.type === 'MultiPolygon'
      ? geometry.coordinates
      : [];

  return polygons.flatMap((polygon) => (
    polygon.flatMap((ring) => ring.map(([lon, lat]) => [lon, lat]))
  ));
}

function ringToPath(ring, crossesDateline) {
  if (!ring?.length) return '';
  return ring
    .map(([lon, lat], index) => {
      const x = adjustedLon(lon, crossesDateline);
      const y = -lat;
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(3)} ${y.toFixed(3)}`;
    })
    .join(' ') + ' Z';
}

export function buildCountryShape(feature) {
  const geometry = feature?.geometry;
  const points = getGeometryPoints(geometry);
  if (!points.length) return null;

  const rawLons = points.map(([lon]) => lon);
  const rawSpan = Math.max(...rawLons) - Math.min(...rawLons);
  const crossesDateline = rawSpan > 180;
  const projected = points.map(([lon, lat]) => [adjustedLon(lon, crossesDateline), -lat]);
  const xs = projected.map(([x]) => x);
  const ys = projected.map(([, y]) => y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const width = Math.max(maxX - minX, 1);
  const height = Math.max(maxY - minY, 1);
  const padding = Math.max(width, height) * 0.14;
  const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
  const paths = polygons.map((polygon) => polygon.map((ring) => ringToPath(ring, crossesDateline)).join(' '));

  return {
    paths,
    viewBox: `${(minX - padding).toFixed(3)} ${(minY - padding).toFixed(3)} ${(width + padding * 2).toFixed(3)} ${(height + padding * 2).toFixed(3)}`,
  };
}
